import type {
  CreatePartnerWithRelatedDataInput,
  CreatePartnerWithRelatedDataPayload as CreatePartnerWithRelatedDataPayloadBase,
} from '@provision/entity-types';
import { gql, QueryHookOptions, useMutation, useQuery } from '@provision/graphql-client';
import { useCallback, useEffect, useMemo } from 'react';
import { isNullish } from 'remeda';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { MAX_QUERY_LIMIT } from 'src/graphql/constants';
import {
  hasPlatformPartnerAccess,
  useGroupRules,
  UserGroupRules,
} from 'src/graphql/entities/group-rules';
import { GET_PARTNER_GROUP_RULE_VALUES_QUERY } from 'src/graphql/entities/group-rules/partner-group-rules';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import { QueryVariables, Records } from 'src/graphql/types';
import { generateFieldModifiersMap } from 'src/graphql/utils';
import { useGlobalStore } from 'src/store';
import { usePartnerStore } from 'src/store/partner';
import type { PartnerRaw } from 'src/types';
import { useNotifications } from 'src/utils/hooks/use-notifications';
import { sortAlphabetically } from 'src/utils/sorting';
import { PARTNER_FIELDS, PARTNER_RELATIONS } from './partner.fragments';

const GET_PARTNERS_QUERY = gql`
  query GetPartner($params: QueryParams) @entity(name: "Partner") {
    getPartner(queryParams: $params) {
      records {
        ...PartnerFields
        ...PartnerRelations
      }
    }
  }
  ${PARTNER_FIELDS}
  ${PARTNER_RELATIONS}
`;

const UPDATE_PARTNER_MUTATION = gql`
  mutation UpdatePartner($partnerId: String!, $data: PartnerDataInput!) {
    updatePartner(
      conditions: [{ attribute: "partnerId", operator: "=", value: $partnerId }]
      data: $data
    ) {
      ...PartnerFields
    }
  }
  ${PARTNER_FIELDS}
`;

const CREATE_PARTNER_WITH_RELATED_DATA_MUTATION = gql`
  mutation CreatePartnerWithRelatedData($params: CreatePartnerWithRelatedDataInput!) {
    createPartnerWithRelatedData(params: $params) {
      partner {
        ...PartnerFields
        ...PartnerRelations
      }
      errors {
        message
        code
      }
    }
  }
  ${PARTNER_FIELDS}
  ${PARTNER_RELATIONS}
`;

interface GetPartnersQuery {
  getPartner: Records<PartnerRaw>;
}

export type CreatePartnerWithRelatedDataPayload =
  CreatePartnerWithRelatedDataPayloadBase<PartnerRaw>;

export type Partner = Pick<
  PartnerRaw,
  | 'partnerId'
  | 'businessName'
  | 'currentPartner'
  | 'timeZone'
  | 'zendeskDomain'
  | 'slackTeamLink'
  | 'notionBoardUrl'
  | 'docsendUrl'
  | 'dosCalendly'
  | 'supportEmail'
  | 'membershipEmail'
  | 'globalNotificationEmail'
> & { id: number; currentPartner: 'yes' | 'no' };

export const usePartners = () => {
  const partners = usePartnerStore((s) => s.partners);
  const partnersRaw = usePartnerStore((s) => s.partnersRaw);
  const loading = usePartnerStore((s) => s.loading);

  return {
    rawPartnerMap: partnersRaw,
    partners: partners || undefined,
    loading,
  };
};

function mapPartnerRawToPartner(partner: PartnerRaw): Partner {
  return {
    id: partner.id,
    timeZone: partner.timeZone,
    partnerId: partner.partnerId,
    businessName: partner.businessName,
    zendeskDomain: partner.zendeskDomain,
    currentPartner: partner.currentPartner as 'yes' | 'no',
    supportEmail: partner.brandGroups?.[0]?.supportEmail ?? partner.supportEmail ?? '',
    membershipEmail:
      partner.brandGroups?.[0]?.membershipEmail ?? partner.membershipEmail ?? '',
    slackTeamLink: partner.slackTeamLink,
    notionBoardUrl: partner.notionBoardUrl,
    docsendUrl: partner.docsendUrl,
    dosCalendly: partner.dosCalendly,
    globalNotificationEmail: partner.globalNotificationEmail,
  };
}

export const useFetchPartners = (options?: QueryHookOptions<GetPartnersQuery>) => {
  const setPartners = usePartnerStore((s) => s.setPartners);
  const setLoading = usePartnerStore((s) => s.setLoading);

  const {
    limitedToIdsList,
    platformPartnerAccess,
    loading: accessLoading,
  } = usePartnerAccess();

  const {
    data,
    loading: queryLoading,
    ...rest
  } = useQuery<GetPartnersQuery, QueryVariables>(GET_PARTNERS_QUERY, {
    ...options,
    variables: {
      params: {
        filters: [
          ...(limitedToIdsList
            ? [
                {
                  attribute: 'partnerId',
                  operator: 'in',
                  value: limitedToIdsList.join(','),
                },
              ]
            : []),
          ...(!platformPartnerAccess
            ? [
                {
                  attribute: 'partnerId',
                  operator: 'not_in',
                  value: String(PLATFORM_PARTNER_ID),
                },
              ]
            : []),
        ],
        limit: MAX_QUERY_LIMIT,
      },
    },
    skip: accessLoading,
    context: { omitIsolationModeHeader: true },
    fetchPolicy: options?.fetchPolicy ?? 'cache-first',
  });

  // keep a single loading flag in Zustand
  useEffect(() => {
    setLoading(accessLoading || queryLoading);
  }, [accessLoading, queryLoading, setLoading]);

  const [partners, rawPartnerMap] = useMemo(() => {
    if (!data?.getPartner?.records?.length) {
      return [null, new Map<number, PartnerRaw>()] as const;
    }

    const byId: Record<string, Partner> = data.getPartner.records.reduce(
      (acc: Record<string, Partner>, partner: PartnerRaw) => {
        acc[partner.partnerId] = mapPartnerRawToPartner(partner);
        return acc;
      },
      {},
    );

    const raw = new Map<number, PartnerRaw>(
      data.getPartner.records.map((p) => [p.id as unknown as number, p]),
    );

    return [byId, raw] as const;
  }, [data?.getPartner?.records]);

  useEffect(() => {
    if (!queryLoading) {
      setPartners(partners, rawPartnerMap);
    }
  }, [partners, rawPartnerMap, queryLoading, setPartners]);

  return {
    ...rest,
    rawPartnerMap,
    partners,
    loading: accessLoading || queryLoading,
  };
};

export function usePartnersOptions(rawPartnerIds?: string[]) {
  // since we fetch all partners for multiple places already
  // there is no need to introduce additional api call for a subset of them
  // so taking all for them and if needed filtering using js
  const { partners } = usePartners();

  return useMemo(() => {
    if (!partners) return [];

    // use incoming ids only if passed, otherwise return all available
    const partnerIds = rawPartnerIds?.length ? rawPartnerIds : Object.keys(partners);

    return partnerIds
      .map((partnerId) => ({
        id: Number(partnerId),
        value: Number(partnerId),
        label: partners[partnerId].businessName,
        supportEmail: partners[partnerId].supportEmail,
        membershipEmail: partners[partnerId].membershipEmail,
      }))
      .sort(sortAlphabetically('label'));
  }, [partners, rawPartnerIds]);
}

export const useSelectedPartner = () => {
  const { partners, loading } = usePartners();
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const selectedPartner = !isNullish(selectedPartnerId) ? partners?.[selectedPartnerId] : null;

  return { selectedPartner, partners, loading };
};

function usePartnerAccess() {
  const { user } = useCurrentUserProfile();
  const { groupRules, loading } = useGroupRules();
  const ruleValue = groupRules[UserGroupRules.AvailablePartnerIds];

  const limitedToIdsList = useMemo(() => {
    if (loading || !user || ruleValue === true) {
      // full access
      return null;
    }

    if (!ruleValue) {
      // own partner access only
      return [user.partnerId];
    }

    const idsFromRule = Array.isArray(ruleValue) ? ruleValue : [ruleValue];
    const filteredIds = hasPlatformPartnerAccess(groupRules)
      ? idsFromRule
      : idsFromRule.filter((id) => id !== PLATFORM_PARTNER_ID);

    // access to limited partners
    return [...filteredIds, user.partnerId];
  }, [loading, user, ruleValue, groupRules]);

  return {
    loading,
    limitedToIdsList,
    platformPartnerAccess: hasPlatformPartnerAccess(groupRules),
  };
}

export const usePartner = () => {
  const { showErrorAlert, showSuccessAlert } = useNotifications();

  const [updatePartnerMutation, { loading: updatePartnerLoading }] = useMutation(
    UPDATE_PARTNER_MUTATION,
    {
      onError: (error) => {
        console.error(error);
        showErrorAlert('Something went wrong updating partner, please try again');
      },
    },
  );

  const updatePartner = useCallback(
    async (partnerId: number, data: Partial<PartnerRaw>) => {
      await updatePartnerMutation({
        variables: {
          partnerId: String(partnerId),
          data,
        },
        optimisticResponse: {
          updatePartner: [
            {
              __typename: 'Partner',
              ...data,
            },
          ],
        },
        update(cache, { data }: { data?: { updatePartner: PartnerRaw[] } }) {
          const updated = data?.updatePartner?.[0];
          if (!updated) return;

          const id = cache.identify({
            __typename: 'Partner',
            id: updated.partnerId,
          });

          cache.modify({
            id,
            fields: generateFieldModifiersMap({
              __typename: 'Partner',
              ...updated,
            }),
          });
        },
        onCompleted: () => {
          showSuccessAlert('Partner has been successfully updated');
        },
        onError: (error) => {
          console.error('Error updating partner:', error);
          showErrorAlert('Failed to update partner. Please try again.');
        },
      });
    },
    [showErrorAlert, showSuccessAlert, updatePartnerMutation],
  );

  return useMemo(
    () => ({ updatePartner, updatePartnerLoading }),
    [updatePartner, updatePartnerLoading],
  );
};

export function useCreatePartnerWithRelatedData() {
  const { showErrorAlert, showSuccessAlert } = useNotifications();
  const setPartners = usePartnerStore((s) => s.setPartners);

  const [createPartnerWithRelatedDataMutation, { loading }] = useMutation<{
    createPartnerWithRelatedData: CreatePartnerWithRelatedDataPayload;
  }>(CREATE_PARTNER_WITH_RELATED_DATA_MUTATION, {
    onError: (error) => {
      console.error('Error creating partner:', error);
      showErrorAlert('Failed to create partner. Please try again.');
    },
  });

  const createPartnerWithRelatedData = useCallback(
    async (params: CreatePartnerWithRelatedDataInput) => {
      const { data } = await createPartnerWithRelatedDataMutation({
        variables: {
          params,
        },
        refetchQueries: [GET_PARTNERS_QUERY, GET_PARTNER_GROUP_RULE_VALUES_QUERY],
        awaitRefetchQueries: true,
      });

      const payload = data?.createPartnerWithRelatedData ?? {
        partner: null,
        errors: [{ message: 'Failed to create partner' }],
      };

      if (!payload.errors.length && payload.partner) {
        const { partners: latestPartners, partnersRaw: latestPartnersRaw } =
          usePartnerStore.getState();
        const nextPartners = {
          ...(latestPartners ?? {}),
          [payload.partner.partnerId]: mapPartnerRawToPartner(payload.partner),
        };
        const nextPartnersRaw = new Map(latestPartnersRaw);
        nextPartnersRaw.set(payload.partner.id as unknown as number, payload.partner);
        setPartners(nextPartners, nextPartnersRaw);
        showSuccessAlert('Partner created successfully');
      }

      return payload;
    },
    [createPartnerWithRelatedDataMutation, setPartners, showSuccessAlert],
  );

  return useMemo(
    () => ({
      createPartnerWithRelatedData,
      loading,
    }),
    [createPartnerWithRelatedData, loading],
  );
}
